<template>
    <div>
        <button
            name="reverse-chargeback"
            class="w-full inline-flex justify-center items-center px-6 py-3 my-2 shadow-sm text-base font-medium rounded-md text-white bg-gray-500 hover:bg-gray-600 focus:outline-none"
            @click="open = true"
        >
            <DocumentRemoveIcon class="h-5 w-5 mr-2" aria-hidden="true" />
            Reverse Chargeback
        </button>

        <TransitionRoot as="template" :show="open">
            <Dialog
                as="div"
                static
                class="fixed z-20 inset-0 overflow-y-auto"
                @close="open = false"
                :open="open"
            >
                <div
                    class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
                >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                    >
                        <DialogOverlay
                            class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                        />
                    </TransitionChild>

                    <span
                        class="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                        >&#8203;</span
                    >
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div
                            class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
                        >
                            <div
                                class="hidden sm:block absolute top-0 right-0 pt-4 pr-4"
                            >
                                <button
                                    type="button"
                                    class="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                                    @click="open = false"
                                >
                                    <span class="sr-only">Close</span>
                                    <XIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="mx-auto shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 sm:mx-0 sm:h-10 sm:w-10"
                                >
                                    <DocumentRemoveIcon
                                        class="h-6 w-6 text-gray-600"
                                        aria-hidden="true"
                                    />
                                </div>

                                <div class="mt-3 sm:mt-0">
                                    <DialogTitle
                                        as="h3"
                                        class="mt-6 sm:mt-2 text-xl leading-6 font-medium text-gray-800 sm:ml-4 text-center sm:text-left"
                                    >
                                        Reversal for Order #{{ publicOrderId }}
                                    </DialogTitle>

                                    <div class="mt-6 mb-4 mx-5 sm:mx-4">
                                        <p
                                            v-if="hasMultipleChargebacks"
                                            class="text-gray-500 mb-2"
                                        >
                                            Reversing a chargeback will delete
                                            it and its corresponding refund
                                            transaction. This cannot be undone.
                                            Select which chargeback to reverse:
                                        </p>
                                        <p v-else class="text-gray-500">
                                            Reversing this chargeback will
                                            delete it and its corresponding
                                            refund transaction. This cannot be
                                            undone.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <form
                                ref="form"
                                :action="actionUrl"
                                method="POST"
                                @submit.prevent="onSubmit(form)"
                            >
                                <div
                                    class="px-4 py-3 sm:px-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense"
                                >
                                    <input
                                        type="hidden"
                                        name="_method"
                                        value="DELETE"
                                    />
                                    <input
                                        type="hidden"
                                        name="_token"
                                        :value="csrf"
                                    />

                                    <div
                                        v-if="hasMultipleChargebacks"
                                        class="sm:col-span-2 sm:px-16 pb-8 sm:pb-6"
                                    >
                                        <label
                                            for="chargeback_id"
                                            class="block text-sm font-medium text-gray-700"
                                        >
                                            Chargeback
                                        </label>

                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <select
                                                v-model="selectedChargebackId"
                                                id="chargeback_id"
                                                class="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-nutreance-green focus:border-nutreance-green sm:text-sm"
                                            >
                                                <option
                                                    v-for="option in chargebackOptions"
                                                    :key="option.id"
                                                    :value="option.id"
                                                >
                                                    {{ option.label }}
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    <div
                                        v-else
                                        class="sm:col-span-2 sm:px-16 pb-8 sm:pb-6"
                                    >
                                        <dl
                                            class="rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
                                        >
                                            <div
                                                class="flex items-baseline justify-between mb-1"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500 text-sm"
                                                >
                                                    Date:
                                                </dt>
                                                <dd
                                                    class="text-gray-800 text-sm"
                                                >
                                                    {{
                                                        chargebackOptions[0]
                                                            .dateDisplay
                                                    }}
                                                </dd>
                                            </div>
                                            <div
                                                class="flex items-baseline justify-between mb-1"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500 text-sm"
                                                >
                                                    Case ID:
                                                </dt>
                                                <dd
                                                    class="text-gray-800 text-sm"
                                                >
                                                    {{
                                                        chargebackOptions[0]
                                                            .caseId
                                                    }}
                                                </dd>
                                            </div>
                                            <div
                                                class="flex items-baseline justify-between"
                                            >
                                                <dt
                                                    class="font-semibold text-gray-500 text-sm"
                                                >
                                                    Amount:
                                                </dt>
                                                <dd
                                                    class="text-gray-800 text-sm"
                                                >
                                                    ${{
                                                        chargebackOptions[0]
                                                            .amountDisplay
                                                    }}
                                                </dd>
                                            </div>
                                        </dl>
                                    </div>

                                    <button
                                        name="yes"
                                        type="submit"
                                        class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-nutreance-green hover:bg-nutreance-green-darker text-base font-medium text-white focus:outline-none sm:col-start-2 sm:text-sm"
                                    >
                                        <LoadingIcon
                                            v-if="submitted"
                                            class="h-5 w-5 mr-2"
                                        />
                                        <CheckIcon
                                            v-else
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        Yes
                                    </button>

                                    <button
                                        name="no"
                                        type="button"
                                        class="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-500 hover:bg-red-600 text-base font-medium text-white focus:outline-none sm:mt-0 sm:col-start-1 sm:text-sm"
                                        @click="open = false"
                                    >
                                        <XIcon
                                            class="h-5 w-5 mr-2"
                                            aria-hidden="true"
                                        />
                                        No
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, DocumentRemoveIcon, XIcon } from "@heroicons/vue/solid";
import { DateTime } from "luxon";
import { useForm } from "../../functions/useForm";
import { useMoney } from "../../functions/useMoney";
import LoadingIcon from "../LoadingIcon.vue";

const props = withDefaults(
    defineProps<{
        csrf?: string;
        publicOrderId?: string;
        existingChargebacks?: any[];
    }>(),
    {
        existingChargebacks: () => [],
    },
);

const { dollarsForDisplay } = useMoney();
const { onSubmit, submitted } = useForm();
const form = ref<any>(null);
const open = ref(false);

const chargebackOptions = computed(() =>
    props.existingChargebacks.map((chargeback: any) => {
        const dateDisplay = DateTime.fromISO(chargeback.date).toLocaleString(
            DateTime.DATE_MED,
        );
        const amountDisplay = dollarsForDisplay(chargeback.amount);
        const caseId = chargeback.case_id || `#${chargeback.id}`;

        return {
            id: chargeback.id,
            caseId,
            amountDisplay,
            dateDisplay,
            label: `Case ${caseId} — $${amountDisplay} — ${dateDisplay}`,
        };
    }),
);

const selectedChargebackId = ref(
    props.existingChargebacks.length ? props.existingChargebacks[0].id : null,
);

const hasMultipleChargebacks = computed(
    () => props.existingChargebacks.length > 1,
);

const actionUrl = computed(() =>
    selectedChargebackId.value
        ? `/user/orders/${props.publicOrderId}/chargebacks/${selectedChargebackId.value}`
        : "#",
);
</script>
